import { SortDescriptor } from "react-aria-components";

import { WorkerPoolScanReference, WorkerPoolUser } from "types/generated";

import { FilterItemKeys } from "./constants";

export type WorkerPoolUsedByItemType = "Stack" | "Module" | "Scan";

export type WorkerPoolUsedByRow = {
  id: string;
  name: string;
  type: WorkerPoolUsedByItemType;
  spaceDetails: { id: string; name: string };
  href: string;
};

export const workerPoolUserToUsedByRow = (user: WorkerPoolUser): WorkerPoolUsedByRow => ({
  id: user.stackId,
  name: user.stackName,
  type: user.isModule ? "Module" : "Stack",
  spaceDetails: user.spaceDetails,
  href: `/${user.isModule ? "module" : "stack"}/${user.stackId}`,
});

export const workerPoolScanToUsedByRow = (scan: WorkerPoolScanReference): WorkerPoolUsedByRow => ({
  id: scan.id,
  name: scan.name,
  type: "Scan",
  spaceDetails: scan.spaceDetails,
  href: `/resources/scans/${scan.id}`,
});

export const compareUsedByRows = (
  a: WorkerPoolUsedByRow,
  b: WorkerPoolUsedByRow,
  sortDescriptor: SortDescriptor
): number => {
  if (!sortDescriptor.column) {
    return 0;
  }

  const isAscending = sortDescriptor.direction === "ascending";

  let valueA = "";
  let valueB = "";

  switch (sortDescriptor.column) {
    case FilterItemKeys.TYPE:
      valueA = a.type;
      valueB = b.type;
      break;
    case FilterItemKeys.NAME:
      valueA = a.name;
      valueB = b.name;
      break;
    case FilterItemKeys.SPACE:
      valueA = a.spaceDetails.name;
      valueB = b.spaceDetails.name;
      break;
  }

  const comparison = isAscending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
  // Stable tie-breaker: without it, items with an equal sort value (e.g. several rows in
  // the same space, sorted by Space) keep whatever relative order they arrived in, which
  // can visibly reshuffle between renders if the input array's order isn't perfectly
  // deterministic (e.g. after a re-fetch).
  return comparison !== 0 ? comparison : a.id.localeCompare(b.id);
};

// The server sorts stacks/modules for the Type column by the boolean `isModule` field — its
// only concept of "type" — which doesn't match the Stack/Module/Scan label-based ordering
// compareUsedByRows uses to merge scans in. Re-sorting entityRows locally for this one column
// keeps both sides of the merge using the same ordering; Name/Space don't need this since the
// server's string-based order there already matches compareUsedByRows exactly.
export const alignEntityRowsForMerge = (
  entityRows: WorkerPoolUsedByRow[],
  sortDescriptor: SortDescriptor
): WorkerPoolUsedByRow[] => {
  if (sortDescriptor.column !== FilterItemKeys.TYPE) {
    return entityRows;
  }

  return [...entityRows].sort((a, b) => compareUsedByRows(a, b, sortDescriptor));
};

export const mergeSortedUsedByRows = (
  a: WorkerPoolUsedByRow[],
  b: WorkerPoolUsedByRow[],
  sortDescriptor: SortDescriptor
): WorkerPoolUsedByRow[] => {
  const merged: WorkerPoolUsedByRow[] = [];
  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    if (compareUsedByRows(a[i], b[j], sortDescriptor) <= 0) {
      merged.push(a[i++]);
    } else {
      merged.push(b[j++]);
    }
  }

  return [...merged, ...a.slice(i), ...b.slice(j)];
};
